import { DragEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import EffectThemeContainer from "../../effectTheme/effectThemeContainer";
import EffectThemeButton from "../../effectTheme/effectThemeButtons";
import EffectThemeImageGrid from "./ImageList";
import { GalleryFolder, ImageStudioGalleryProps } from "../../../types/imageStudio/imageLists";
import ImageProps from "../../../types/imageStudio/images";
import { fileToImageProps } from "../../../utils/images/imagesFunctions";
import { faDownload, faSpinner, faTrashCan, faX } from "@fortawesome/free-solid-svg-icons";
import Cookies from "js-cookie";
import { ApiDataStorage } from "../../../apis/ApiDataStorage";
import { deleteProjectData, stripKnownImageStudioExtensions } from "../../../utils/aiModels/apiImageStudio";
import { ContextProps } from "../../../types/imageStudio/contextMenu";
import { ContextMenuImageGallery } from "./ContextMenu";
import { handleShowToast } from "../../toasts/toasts";
import { GalleryInfoElement } from "../../../utils/aiModels/infotexts";
import { InfoPopoverButton } from "./InfoButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const ImageStudioGallery = ({
    show,
    folderActive,
    currentImages,
    currentImageObjects,
    historyOfUsedImages,
    allImages,
    History_Cookie_name,
    MAIN_DATA_PATH_IMAGES,
    baseImage,
    clearProject,
    onChangeAllImages,
    onChangeCurrentImages,
    onChangeCurrentImagesObject,
    onChangeHistoryOfUsedImages,
    onFolderChange,
    onImageHover,
    onImageHoverEnd,
    onImageSelect,
    onUploadImage,
    onGalleryImageDragStart,
    onAddGalleryImageAsLayer,
    onDeleteAllImages,
    onDownloadAllImages,
    infoDeleteDataElementID
}: ImageStudioGalleryProps) => {
    const { t } = useTranslation("imageStudio");
    const baseHeight = 220;
    const minHeight = 200;
    const maxHeight = window.innerHeight * 0.75;
    const isDeletingRef = useRef(false); //avoid delete and select handling simutainously
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const [bulkAction, setBulkAction] = useState<"download" | "delete" | null>(null);
    const [height, setHeight] = useState(baseHeight);
    const [images, setImages] = useState<ImageProps[]>([]);
    const resizeRef = useRef<{
        startY: number;
        startHeight: number;
    } | null>(null);
    const [galleryContextMenu, setGalleryContextMenu] = useState<ContextProps>({
        visible: false,
        x: 0,
        y: 0
    });

    const [galleryContextItem, setGalleryContextItem] = useState<{
        index: number;
        image: ImageProps;
        folder: GalleryFolder;
    } | null>(null);

    const selectedGalleryIndex = baseImage?.file
        ? images.findIndex(
            img =>
                stripKnownImageStudioExtensions(img.file?.name ?? img.title ?? "") ===
                stripKnownImageStudioExtensions(baseImage.file?.name ?? baseImage.title ?? "")
        )
        : null;

    const updateImagesLists = async() => {
        const targetList = folderActive === "Current"
            ? currentImages
            : folderActive === "History"
                ? historyOfUsedImages
                : allImages;
        const convertedList:ImageProps[] = [];
        for(let item of targetList){
            const img = await fileToImageProps(item);
            convertedList.push(img);
        };
        setImages(convertedList);
    };

    const deleteItemInFolder = async(index:number, targetFolder?: GalleryFolder) => {
        if(!images || !images[index].file) return;
        isDeletingRef.current = true;
        setTimeout(() => {
            isDeletingRef.current = false;
        }, 0);
        handleShowToast(infoDeleteDataElementID,1000);
        const fileName = images[index].file.name;
        const baseName = fileName.replace(/\.[^.]+$/, "");
        const folder = targetFolder ?? folderActive;
        if(folder === 'All'){
            //update history
            const historyListIndex = historyOfUsedImages.map(file=>file.name).indexOf(fileName);
            if(historyListIndex !== -1){
                const history = [...historyOfUsedImages];
                history.splice(historyListIndex,1);
                onChangeHistoryOfUsedImages(history);
                const newCookie = history.map(file=>file.name)
                Cookies.set(History_Cookie_name,JSON.stringify(newCookie));
            }
            //update all
            const allListindex = allImages.map(file => file.name).indexOf(fileName);
            if(allListindex !== -1){
                const allListImages = [...allImages];
                allListImages.splice(allListindex,1);
                onChangeAllImages(allListImages);
                
                //delete thumbnail and project zip
                await deleteProjectData(MAIN_DATA_PATH_IMAGES,baseName);
            }

        }
        else if (folder === 'Current'){
            //update current
            const currentListIndex = currentImages.map(file => file.name).indexOf(fileName);
            if(currentListIndex !== -1){
                const currentList = [...currentImages];
                currentList.splice(currentListIndex,1);
                onChangeCurrentImages(currentList);

                const currentObjects = [...currentImageObjects];
                const projectBasenames = currentObjects.map(item => stripKnownImageStudioExtensions(item.image?.file?.name ?? ''));
                const idx = projectBasenames.indexOf(stripKnownImageStudioExtensions(fileName));
                if(index > -1){
                    currentObjects.splice(idx,1);
                    onChangeCurrentImagesObject(currentObjects);
                }
            }
        }
        else{
            //update history
            const historyListIndex = historyOfUsedImages.map(file=>file.name).indexOf(fileName);
            if(historyListIndex !== -1){
                const history = [...historyOfUsedImages];
                history.splice(historyListIndex,1);
                onChangeHistoryOfUsedImages(history);
                Cookies.set(History_Cookie_name,JSON.stringify(history.map(file=>file.name)));
            }
        }
        clearProject();
    };

    const addItemToFolder = async (file:File) => {
        onUploadImage(file);
    };
    const handleDownloadAll = async () => {
        if (bulkAction || allImages.length === 0) return;

        setBulkAction("download");

        try {
            await onDownloadAllImages();
        } finally {
            setBulkAction(null);
        }
    };

    const handleConfirmDeleteAll = async () => {
        if (bulkAction || allImages.length === 0) return;

        setBulkAction("delete");

        try {
            await onDeleteAllImages();
            setShowDeleteAllModal(false);
        } finally {
            setBulkAction(null);
        }
    };

    useEffect(() => {
        const handleMove = (event: MouseEvent) => {
            const current = resizeRef.current;
            if (!current) return;

            const delta = current.startY - event.clientY;
            const nextHeight = Math.max(
                minHeight,
                Math.min(maxHeight, current.startHeight + delta)
            );

            setHeight(nextHeight);
        };

        const handleUp = () => {
            resizeRef.current = null;
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };

        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);

        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, []);

    useEffect(()=>{
        updateImagesLists();
    },[show,folderActive,currentImages,historyOfUsedImages,allImages]);

    if (!show) return null;

    return (
        <>
        {showDeleteAllModal && (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                    zIndex: 50000,
                    background: "rgba(0,0,0,0.55)"
                }}
            >
                <EffectThemeContainer
                    containerClass="p-4 border rounded"
                    children={
                        <div style={{
                                width: "min(420px, 90vw)"
                            }}
                        >
                            <h5>
                                {t("gallery.deleteAllConfirm.title")}
                            </h5>

                            <p className="text-secondary">
                                {t("gallery.deleteAllConfirm.description",{count:allImages.length})}
                            </p>

                            <p className="fw-semibold">
                                {t("gallery.deleteAllConfirm.warning")}
                            </p>

                            <div className="d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    disabled={bulkAction ==="delete"}
                                    onClick={() =>
                                        setShowDeleteAllModal(
                                            false
                                        )
                                    }
                                >
                                    {t("gallery.deleteAllConfirm.cancel")}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    disabled={bulkAction ==="delete"}
                                    onClick={() => {void handleConfirmDeleteAll();}}
                                >
                                    <FontAwesomeIcon
                                        icon={bulkAction === "delete" ? faSpinner : faTrashCan}
                                        spin={bulkAction === "delete"}
                                        className="me-2"
                                    />

                                    {bulkAction ==="delete"
                                        ? t("gallery.deleteAllConfirm.deleting")
                                        : t("gallery.deleteAllConfirm.confirm")}
                                </button>
                            </div>
                        </div>
                    }
                />
            </div>
        )}

        <div
            className="position-relative px-2 mb-2 w-100"
            style={{
                height: baseHeight,
                minHeight: baseHeight,
                overflow: "visible",
                zIndex: 25000
            }}
        >
            <EffectThemeContainer
                containerClass="position-absolute start-0 end-0 bottom-0 m-0 p-1 border rounded border-secondary"
                children={
                    <div
                        className="d-flex flex-column p-0 m-0"
                        style={{
                            height,
                            maxHeight,
                            minHeight,
                            overflow: "hidden"
                        }}
                        onDragOver={(e) => {
                            const file = e.dataTransfer.items?.[0];
                            if (!file || file.kind !== "file") return;

                            e.preventDefault();
                            e.dataTransfer.dropEffect = "copy";
                        }}
                        onDrop={async (e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (!file || !file.type.startsWith("image/")) return;

                            await addItemToFolder(file);
                        }}
                    >
                        

                        <div className="row m-0 p-0 ps-1 mb-1">
                            <div className="col-auto p-0">
                                <EffectThemeButton
                                    className="btn border-secondary rounded w-100 rounded-start-only"
                                    title={t("galleryTabs.current")}
                                    active={folderActive === "Current"}
                                    onClick={() => onFolderChange("Current")}
                                    tabPosition="left"
                                >
                                    {t("galleryTabs.current")} ({currentImages.length})
                                </EffectThemeButton>
                            </div>

                            <div className="col-auto p-0">
                                <EffectThemeButton
                                    className="btn border-secondary w-100 rounded-0"
                                    title={t("galleryTabs.history")}
                                    active={folderActive === "History"}
                                    onClick={() => onFolderChange("History")}
                                    tabPosition="left"
                                >
                                    {t("galleryTabs.history")} ({historyOfUsedImages.length})
                                </EffectThemeButton>
                            </div>

                            <div className="col-auto p-0">
                                <EffectThemeButton
                                    className="btn border-secondary rounded w-100 rounded-end-only"
                                    title={t("galleryTabs.all")}
                                    active={folderActive === "All"}
                                    onClick={() => onFolderChange("All")}
                                    tabPosition="left"
                                >
                                    {t("galleryTabs.all")} ({allImages.length})
                                </EffectThemeButton>
                            </div>
                            <div
                                className="col p-0"
                                style={{
                                    cursor: "ns-resize"
                                }}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    resizeRef.current = {
                                        startY: event.clientY,
                                        startHeight: height
                                    };
                                    document.body.style.userSelect = "none";
                                    document.body.style.cursor = "ns-resize";
                                }}
                            />
                            <div className="col-auto p-0 pe-2">
                                <button
                                    type="button"
                                    className="btn border-0 bg-transparent p-1"
                                    title={t("gallery.downloadAll")}
                                    disabled={bulkAction !== null || allImages.length === 0}
                                    onClick={() => void handleDownloadAll()}
                                    children={
                                        <FontAwesomeIcon
                                            icon={bulkAction === "download" ? faSpinner : faDownload}
                                            spin={bulkAction === "download"}
                                        />
                                    }
                                />
                            </div>
                            <div className="col-auto p-0 pe-2">
                                <button 
                                    type="button"
                                    className="btn border-0 bg-transparent p-1"
                                    title={t("gallery.deleteAll")}
                                    disabled={bulkAction !== null || allImages.length === 0}
                                    onClick={() => setShowDeleteAllModal(true)}
                                    children={
                                        <FontAwesomeIcon 
                                            icon={bulkAction === "delete" ? faSpinner : faTrashCan} 
                                            spin={bulkAction === "delete"}
                                        /> 
                                    }
                                />
                            </div>
                            <div className="col-auto p-0 pe-2">
                                <InfoPopoverButton
                                    children={<GalleryInfoElement/>}
                                    width={420}
                                />
                            </div>
                        </div>
                        

                        <div className="flex-grow-1 overflow-y-auto p-1">
                            <EffectThemeImageGrid
                                size="150px"
                                images={images}
                                onImageHover={(idx, img) => onImageHover?.(idx,img)}
                                onImageHoverEnd={onImageHoverEnd}
                                onImageSelect={(idx, img) => {
                                    if (isDeletingRef.current) return;
                                    onImageSelect?.(idx, img);
                                }}
                                onDeleteItem={deleteItemInFolder}
                                titleDelete={folderActive === 'All' ? t('page.deletePermanently') : t('page.remove')}
                                titlteImage={t('page.selectImage')}
                                iconDelete={folderActive === 'All' ? faTrashCan : faX}
                                draggableImages={true}
                                onImageDragStart={onGalleryImageDragStart}
                                selectedImageIndex={
                                    selectedGalleryIndex !== null && selectedGalleryIndex >= 0
                                        ? selectedGalleryIndex
                                        : null
                                }
                                onImageContextMenu={(event, index, img) => {
                                    setGalleryContextItem({
                                        index,
                                        image: img,
                                        folder: folderActive
                                    });

                                    setGalleryContextMenu({
                                        visible: true,
                                        x: event.clientX,
                                        y: event.clientY
                                    });
                                }}
                                
                            />
                            {galleryContextItem && (
                                <ContextMenuImageGallery
                                    index={galleryContextItem.index}
                                    image={galleryContextItem.image}
                                    folder={galleryContextItem.folder}
                                    contextMenu={galleryContextMenu}
                                    setContextMenu={setGalleryContextMenu}
                                    onRemoveItem={deleteItemInFolder}
                                    onOpenProject={(index, img) => onImageSelect?.(index, img)}
                                    onAddAsImageLayer={(index, img) => onAddGalleryImageAsLayer?.(index, img)}
                                    baseImage={baseImage}                                    
                                />
                            )}
                        </div>
                    </div>
                }
            />
        </div>
        </>
    );
};